import React from 'react';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';

/**
 * Integrations tab data (spec §5.5) — design-only/mock. Communicates BITE as a
 * compliance data ecosystem (data flowing in and out), not a repository. One
 * integration (`featured: true`) gets a fleshed-out bidirectional-sync detail.
 */

export type IntegrationStatus = 'Connected' | 'Available' | 'Coming Soon';
export type IntegrationDirection = 'inbound' | 'outbound' | 'bidirectional';

export interface IntegrationCard {
  key: string;
  name: string;
  category: string;
  description: string;
  status: IntegrationStatus;
  direction: IntegrationDirection;
  icon: React.ReactNode;
  featured?: boolean;
}

const icon = (El: React.ElementType): React.ReactNode => (
  <El style={{ fontSize: 22 }} />
);

export const INTEGRATIONS: IntegrationCard[] = [
  {
    key: 'erp',
    name: 'ERP / SAP Connector',
    category: 'ERP',
    description:
      'Pull product master, BOMs, and order data in; push compliance flags back to the source of record.',
    status: 'Available',
    direction: 'bidirectional',
    icon: icon(StorageOutlinedIcon),
    featured: true,
  },
  {
    key: 'api',
    name: 'API / Webhook',
    category: 'API',
    description:
      'REST API and outbound webhooks for screening results, license status changes, and vetted packages.',
    status: 'Available',
    direction: 'bidirectional',
    icon: icon(ApiOutlinedIcon),
  },
  {
    key: 'sftp',
    name: 'SFTP / File Drop',
    category: 'File',
    description:
      'Scheduled CSV/flat-file imports and exports for environments without a live API.',
    status: 'Coming Soon',
    direction: 'inbound',
    icon: icon(CloudSyncOutlinedIcon),
  },
  {
    key: 'export-mgmt',
    name: 'Export / License Management',
    category: 'Filing',
    description:
      'Push vetted product packages and screening evidence downstream to your filing and license-management system.',
    status: 'Coming Soon',
    direction: 'outbound',
    icon: icon(LocalShippingOutlinedIcon),
  },
  {
    key: 'entity-db',
    name: 'BITE Entity Database',
    category: 'Screening',
    description:
      'Live entity data for customer and consignee linking.',
    status: 'Connected',
    direction: 'inbound',
    icon: icon(HubOutlinedIcon),
  },
];

export const FEATURED_INTEGRATION = INTEGRATIONS.find((i) => i.featured) as
  | IntegrationCard
  | undefined;

// Mock CSV column-mapping (source field → BITE product field), spec §5.5.
export interface FieldMapping {
  source: string;
  target: string;
  sample: string;
}

export const FEATURED_FIELD_MAPPINGS: FieldMapping[] = [
  { source: 'MATNR (Material No.)', target: 'internal_sku', sample: 'IMU-4400-A' },
  { source: 'MAKTX (Description)', target: 'description', sample: 'Inertial Measurement Unit' },
  { source: 'HERKL (Country of Origin)', target: 'country_of_origin', sample: 'United States' },
  { source: 'STAWN (Commodity Code)', target: 'hts_code', sample: '9014.20.8040' },
  { source: 'ZECCN (Export Class)', target: 'jurisdiction_details.eccn', sample: '7A994' },
  { source: 'ZEND_USE (End Use)', target: 'intended_end_use', sample: 'Avionics navigation' },
  { source: 'MEINS (Base Unit)', target: 'unit_of_measure', sample: 'EA' },
];

const STATUS_VARIANT: Record<IntegrationStatus, string> = {
  Connected: 'success',
  Available: 'info',
  'Coming Soon': 'muted',
};

export const IntegrationStatusPill: React.FC<{ value: IntegrationStatus }> = ({
  value,
}) => (
  <span className={`eh-pill eh-pill-${STATUS_VARIANT[value]}`}>{value}</span>
);

export const directionLabel = (d: IntegrationDirection): string =>
  d === 'bidirectional'
    ? 'Two-way sync'
    : d === 'inbound'
    ? 'Inbound'
    : 'Outbound';
